/**
 * Named timers and abort controllers of one client. Setting a name again
 * replaces what ran under it, and `clearAll` stops everything, so a stopped
 * client leaves nothing running.
 */
export class Timers {
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly controllers = new Map<string, AbortController>();

  after(name: string, ms: number, run: () => void): void {
    this.clear(name);
    this.timers.set(
      name,
      setTimeout(() => {
        this.timers.delete(name);
        run();
      }, ms),
    );
  }

  every(name: string, ms: number, run: () => void): void {
    this.clear(name);
    this.timers.set(name, setInterval(run, ms));
  }

  has(name: string): boolean {
    return this.timers.has(name);
  }

  clear(name: string): void {
    // clearTimeout also cancels intervals: browsers and Node keep one list of timers.
    clearTimeout(this.timers.get(name));
    this.timers.delete(name);
  }

  abortable(name: string): AbortController {
    this.abort(name);
    const controller = new AbortController();
    this.controllers.set(name, controller);
    return controller;
  }

  abort(name: string): void {
    this.controllers.get(name)?.abort();
    this.controllers.delete(name);
  }

  /** The work finished: forget its controller without aborting it. */
  release(name: string, controller: AbortController): void {
    if (this.controllers.get(name) === controller) this.controllers.delete(name);
  }

  clearAll(): void {
    for (const timer of this.timers.values()) clearTimeout(timer);
    this.timers.clear();
    for (const controller of this.controllers.values()) controller.abort();
    this.controllers.clear();
  }
}
